import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './index.module.less';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { InstagramQueryPostItemModel } from '@/types/anonymously.interface';
import { formatNumberToK } from '@/shared/utils';
import { useUserProvider } from '@/context/UserProvider';
import { fetchInsStoryPosts, fetchInsStoryReels } from '@/lib/service/anonymously';
import { Button, Spin } from 'antd';
import { DownloadIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import dayjs from 'dayjs';
import { LimitAlert } from '@/components/LandingPage/FeeLimitAlert';
import CommonSimpleModal from '@/components/Common/CommonSimpleModal';

// CLS 优化：关键组件使用同步导入，避免异步加载导致的布局偏移
import LocalImage from '@/components/Common/LocalImage';
import ImageGallery from '@/components/Common/ImageGallery';
import PostsAnalysis from '../PostsAnalysis';
import useStoryPremium from '@/lib/hooks/isPremium/useStoryPremium';
import useTurnstileVerify from '@/lib/hooks/anonymous-instagram-story-viewer/useTurnstileVerify';
import TurnstileModal from '@/components/TurnstileModal';

const PostResultContent = ({
  showDownload = false,
  hideTitle = false,
  locked = false,
  loadMoreNotCheckLogin,
  page_token,
  dataList,
  gotoPlanClick,
  analyzeAll,
  type = 'post',
}: {
  showDownload?: boolean;
  hideTitle?: boolean;
  locked?: boolean;
  loadMoreNotCheckLogin?: boolean;
  page_token?: string;
  dataList?: InstagramQueryPostItemModel[];
  gotoPlanClick?: () => void;
  analyzeAll?: boolean;
  type?: 'post' | 'reels';
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [show, setShow] = useState(false);
  const [pageToken, setPageToken] = useState(page_token);
  const [index, setIndex] = useState(1);
  const [postList, setPostList] = useState<InstagramQueryPostItemModel[]>([]);
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const [loading, setLoading] = useState(false);
  const [showLimit, setShowLimit] = useState(false);
  const [alertInfo, setAlertInfo] = useState<any>(null);
  const { isPremium } = useStoryPremium();
  const { turnstileRef, getTurnstileToken } = useTurnstileVerify();

  const showMore = useMemo(() => {
    if (locked) {
      return false;
    }
    if (showDownload) {
      return index * 12 < dataList?.length;
    } else {
      return !!pageToken;
    }
  }, [index, dataList, showDownload, pageToken]);

  const showPostList = useMemo(() => {
    if (showDownload) {
      if (index * 12 < dataList?.length) {
        return dataList?.slice(0, index * 12);
      } else {
        return dataList;
      }
    } else {
      return postList;
    }
  }, [index, dataList, showDownload, postList]);

  useEffect(() => {
    setCurrentIndex(0);
    setShow(false);
    setPostList(dataList);
    setPageToken(page_token);
    setIndex(1);
  }, [dataList, page_token]);

  const onSearch = async () => {
    if (!loadMoreNotCheckLogin && !isLogin) {
      onOpenLoginModal(true);
      onChangedLoginView(false);
      return;
    }

    if (showDownload) {
      setIndex(() => index + 1);
      return;
    }

    const media_name = postList?.[0]?.media_name;

    if (!media_name || !pageToken) return;

    if (loading) return;

    const turnstileToken = await getTurnstileToken();
    if (turnstileToken === null) return;

    setLoading(true);

    const fetchFun = type === 'post' ? fetchInsStoryPosts : fetchInsStoryReels;

    fetchFun(media_name, pageToken, turnstileToken ?? '', {
      bizFuncName: 'stories_viewer',
      isPremium,
    })
      .then((res) => {
        if (res?.code === 0) {
          const TempItems = res?.data?.items?.map((item) => {
            const mediaList = item.carousel_media || item.media_list;
            const hasVideoInCarousel = mediaList?.some((media) => media.media_type === 2);
            return {
              ...item,
              media_list: mediaList,
              caption: typeof item.caption === 'string' ? item.caption : item.caption?.text,
              cover_image: item.thumbnail_url || item.cover_image,
              is_video: hasVideoInCarousel ?? item.is_video ?? false,
            };
          });
          setPostList([...postList, ...(TempItems as InstagramQueryPostItemModel[])]);
          setPageToken(res?.data?.page_token);
          return;
        } else {
          setPageToken(null);
          setAlertInfo({
            title: t('Warning'),
            desc: res?.message || t('An error occurred. Please try again later.'),
            btnTitle: t('OK'),
            type: 'API-Error',
          });
        }
      })
      .catch(() => {
        setPageToken(null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const isSingleMedia = useCallback((item: InstagramQueryPostItemModel) => {
    return item?.media_list?.length === 1;
  }, []);

  const handleDownload = async (postItemModel: InstagramQueryPostItemModel) => {
    if (!postItemModel) {
      return;
    }

    let imgUrlStr;
    const currentMedia = postItemModel?.media_list[0];
    if (postItemModel?.media_list?.length > 0) {
      imgUrlStr = currentMedia?.media_url;
    } else {
      imgUrlStr = postItemModel?.cover_image;
    }

    if (!imgUrlStr) return;

    try {
      // const response = await fetch(`/n-api/download-image?url=${encodeURIComponent(imgUrlStr)}`);
      // if (!response.ok) {
      //   throw new Error('Failed to download file');
      // }
      //
      // const blob = await response.blob();
      // const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = imgUrlStr.includes('pumpsoul.com')
        ? `${imgUrlStr}?dl=1`
        : `/n-api/download-image?url=${encodeURIComponent(imgUrlStr)}`;
      a.download = `Dolphin_Radar_${dayjs().format('YYYYMMDDHHmmss')}.${currentMedia?.media_type === 2 ? 'mp4' : 'png'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  return (
    <>
      {dataList?.length > 0 && (
        <PostsAnalysis post_list={dataList} analyzeAll={analyzeAll} locked={locked} gotoPlanClick={gotoPlanClick} />
      )}
      <div className={styles.content}>
        {!hideTitle && (
          <div className={styles.sectionTitle}>
            <span className={styles.count}>{postList?.length ?? 0}</span>{' '}
            {type === 'post' ? t('New Posts') : t('New Reels')}
          </div>
        )}

        {dataList?.length > 0 ? (
          <>
            <div className={styles.listContainer}>
              {showPostList?.map((item: InstagramQueryPostItemModel, index: number) => {
                return (
                  <div
                    className={cn(styles.item, 'group')}
                    key={index}
                    onClick={() => {
                      setCurrentIndex(index);
                      setShow(true);
                    }}
                  >
                    <LocalImage
                      alt=""
                      // 搜索时返回的图片字段是 cover_image，而分页加载时返回的图片字段是 thumbnail_url
                      src={item?.cover_image || item?.thumbnail_url}
                      style={{ width: '100%', height: '100%' }}
                    />
                    {item?.is_video && isSingleMedia(item) && (
                      <Image
                        alt=""
                        src={'/images/toolkit/video-small-icon.webp'}
                        width={20}
                        height={20}
                        loading="lazy"
                        style={{ position: 'absolute', top: '15px', right: '15px' }}
                      />
                    )}
                    {!isSingleMedia(item) && (
                      <Image
                        alt=""
                        src={'/images/toolkit/img-small-icon.webp'}
                        width={20}
                        height={20}
                        loading="lazy"
                        style={{ position: 'absolute', top: '15px', right: '15px' }}
                      />
                    )}
                    <div className={styles.infoContainer}>
                      <div className={styles.infoDataContainer}>
                        <Image alt="" src={'/images/toolkit/comment-icon.webp'} width={20} height={20} loading="lazy" />
                        <span style={{ marginRight: '10px' }}>{formatNumberToK(item?.comment_count)}</span>
                        <Image alt="" src={'/images/toolkit/like-icon.webp'} width={20} height={20} loading="lazy" />
                        <span>{formatNumberToK(item?.like_count)}</span>
                      </div>
                      {item?.publish_time && <div>{item?.publish_time}</div>}
                      <Button
                        icon={<DownloadIcon className="!flex mx-auto" size={20} />}
                        className={cn('absolute bottom-4 right-4 flex group-hover:block hidden', {
                          '!hidden': !isSingleMedia(item),
                        })}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(item);
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CLS 优化：More 按钮 loading 时显示骨架屏预占位，避免新数据加载后布局偏移 */}
            {loading && (
              <div className={styles.listContainer} style={{ marginTop: '24px' }}>
                {Array.from({ length: 12 }, (_, i) => (
                  <div key={`skeleton-${i}`} className={styles.item}>
                    <div className={styles.skeletonInner} />
                  </div>
                ))}
              </div>
            )}

            {showMore && (
              <div className={styles.moreButton} onClick={onSearch}>
                {loading ? <Spin size="small" /> : t('More')}
              </div>
            )}

            {showMore && !showDownload && <div className={styles.line} />}
          </>
        ) : (
          <>
            {!(dataList?.length > 0) && (
              <div className={styles.noData}>
                <span>{t('The account you entered has no posts.')}</span>
              </div>
            )}
          </>
        )}
      </div>

      {show && (
        <ImageGallery
          dataList={postList}
          index={currentIndex}
          show={show}
          onClose={() => setShow(false)}
          gotoPlanClick={gotoPlanClick}
        />
      )}

      <LimitAlert
        open={showLimit}
        closeAction={(isOk) => {
          setShowLimit(false);
          if (isOk) {
            setTimeout(() => {
              gotoPlanClick && gotoPlanClick();
              const dom = document.getElementById('1');
              dom && dom.scrollIntoView({ behavior: 'smooth' });
            }, 20);
          }
        }}
      />
      <CommonSimpleModal
        open={alertInfo !== null}
        title={alertInfo?.title}
        desc={alertInfo?.desc}
        btnTitle={alertInfo?.btnTitle}
        closeAction={() => {
          setAlertInfo(null);
        }}
      />
      <TurnstileModal ref={turnstileRef} />
    </>
  );
};

export default PostResultContent;
